<template>
  <div class="target-build-history">
    <b-loading is-full-page :active="isLoading"></b-loading>
    <b-notification v-if="error" type="is-danger is-light" aria-close-label="Close notification" role="alert">
      {{ error }}
    </b-notification>
    <div class="card">
      <div class="card-content">
        <div class="columns is-tablet">
          <div class="column">
              <b-field label="Products">
                  <b-taginput
                      v-model="selectedProducts"
                      :data="filteredProducts"
                      autocomplete
                      open-on-focus
                      icon="label"
                      field="name"
                      placeholder="Add a product"
                      @typing="queries.product = $event">
                  </b-taginput>
              </b-field>
              <b-field label="Branch">
                  <b-taginput
                      v-model="selectedBranches"
                      :data="filteredBranches"
                      autocomplete
                      open-on-focus
                      icon="label"
                      field="name"
                      placeholder="Add a branch"
                      @typing="queries.branch = $event">
                  </b-taginput>
              </b-field>
              <b-field label="Commit id">
                  <b-input v-model="filters.commit_id"/>
              </b-field>
          </div>
          <div class="column">
              <b-field label="CI service">
                  <b-taginput
                      v-model="selectedCIServices"
                      :data="filteredCIServices"
                      autocomplete
                      open-on-focus
                      icon="label"
                      field="name"
                      placeholder="Add a CI service"
                      @typing="queries.ciService = $event">
                  </b-taginput>
              </b-field>
              <b-field label="Build trigger">
                  <b-taginput
                      v-model="selectedBuildTypes"
                      :data="filteredBuildTypes"
                      autocomplete
                      open-on-focus
                      icon="label"
                      field="name"
                      placeholder="Add a build trigger"
                      @typing="queries.buildType = $event">
                  </b-taginput>
              </b-field>
              <b-field label="Target (regexp)">
                  <b-input v-model="filters.target_name"/>
              </b-field>
            <b-field label="Time interval">
              <div class="field">
                  <b-select expanded v-model="filters.time_interval">
                      <option
                          v-for="option in TIME_INTERVAL_OPTIONS"
                          :value="option.value.toString()"
                          :key="option.value">
                          {{ option.title }}
                      </option>
                  </b-select>
                <b-datepicker
                        v-if="filters.time_interval === '-2'"
                        placeholder="Click to select..."
                        v-model="filters.dates"
                        icon="calendar-today"
                        range>
                </b-datepicker>
              </div>
            </b-field>
          </div>
        </div>
        <b-button type="is-primary" @click="onSearchClick">Search</b-button>
      </div>
    </div>
    <br>

    <TargetBuildTable
            id="tablePagination"
            :link-generator="generateResultsLink"
            :target-builds="targetBuildHistory">
      <template #empty-message>
        <span v-if="!dataIsLoaded">
            Set filters and click "Search"
        </span>
        <span v-else>
            No results found
        </span>
      </template>
    </TargetBuildTable>
    <b-pagination
            class="target-build-history-pagination"
            :total="targetBuildHistoryTotalCount"
            :current="currentPage"
            size="is-small"
            v-on:change="onChangePage"
            :per-page="PER_PAGE"
            order="is-right"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page">
    </b-pagination>
  </div>
</template>

<script>
    import {
        TARGET_BUILDS_HISTORY_PER_PAGE,
        TIME_INTERVAL_OPTIONS,
        CI_SERVICES,
    } from "../utils/constants";
    import {mapActions, mapGetters, mapMutations} from "vuex";
    import filtersMixin from "../utils/filtersMixin";
    import {formatDate, valueToArray, filterOptionsByQuery} from "../utils/helpers";
    import TargetBuildTable from "../components/TargetBuildTable";

    export default {
        name: "TargetBuildHistory",
        mixins: [filtersMixin],
        components: {
            TargetBuildTable,
        },
        data() {
            return {
                isLoading: false,
                error: null,
                filters: {},
                PER_PAGE: TARGET_BUILDS_HISTORY_PER_PAGE,
                TIME_INTERVAL_OPTIONS,
                CI_SERVICES,
                dataIsLoaded: false,

                queries: {
                    product: '',
                    branch: '',
                    target_name: '',
                    ciService: '',
                    buildType: '',
                },
            }
        },
        computed: {
            ...mapGetters('TargetBuilds', [
                'targetBuildHistory',
                'targetBuildHistoryTotalCount',
            ]),
            ...mapGetters('Auth', [
                'hasAccess',
            ]),
            currentPage() {
                return parseInt(this.$route.query.page || 1);
            },
        },
        methods: {
            ...mapActions('TargetBuilds', [
                'fetchTargetBuildHistory',
                'fetchBranches',
                'fetchProducts',
                'fetchBuildTypes',
                'fetchCIServices',
            ]),
            ...mapMutations('TargetBuilds', [
                'CLEAR_TARGET_BUILD_HISTORY',
            ]),
            setFiltersByQuery(query) {
                this.filters = {
                    target_name: query.target_name || '',
                    branch_ids: valueToArray(query.branch_ids),
                    product_ids: query.product_ids == null ? ["2"] : valueToArray(query.product_ids),
                    time_interval: query.time_interval == null ? '10' : query.time_interval,
                    dates: valueToArray(query.dates).map(item => new Date(item)),
                    ci_service_ids: valueToArray(query.ci_service_ids),
                    commit_id: query.commit_id || '',
                    build_type_ids: valueToArray(query.build_type_ids),
                }
            },
            fetchData() {
                return [
                    this.fetchBranches(),
                    this.fetchProducts(),
                    this.fetchBuildTypes(),
                    this.fetchCIServices(),
                ];
            },
            async setFiltersByName() {},
            async search() {
                if (!this.hasAccess) return;
                this.isLoading = true;
                const filters = Object.assign({}, this.filters, {
                    page: this.currentPage,
                    per_page: this.PER_PAGE,
                    dates: this.filters.dates.map(date => formatDate(date)),
                });
                try {
                    await this.fetchTargetBuildHistory({ filters });
                    this.dataIsLoaded = true;
                } catch(message) {
                    this.error = message;
                }
                this.isLoading = false;
            },
            async onChangePage(page) {
                const query = Object.assign({}, this.$route.query, { page, rowsPage: 1 });
                await this.$router.push({ name: this.$route.name, query });
                await this.search();
                this.$scrollTo('#tablePagination');
            },
            clearData() {
                this.CLEAR_TARGET_BUILD_HISTORY();
            },
            generateResultsLink(targetBuild) {
                return {
                    name: 'TestRunIndex',
                    query: {
                        target_name: targetBuild.target,
                        show_skipped_results: true,
                    }
                };
            },
            async afterSearch() {
                this.$scrollTo('#tablePagination');
            }
        },
    }
</script>

<style scoped>
  .target-build-history-pagination {
    padding: 20px 0;
  }
</style>